// Audit helper for the "Check Primary Contacts" modal: find active DFV Services
// that have no primary contact nominated, split into two groups because they are
// different problems to chase:
//   • hasContactsNoPrimary — the service has contact(s) but none is marked Primary
//     (fixable in-app: open the service, Change Primary, pick one).
//   • noContactsAtAll       — the service has no contacts at all (needs someone
//     added before a Primary can exist).
//
// The DB reads live in src/data (listSheltersMissingPrimary +
// listShelterIdsWithActiveContacts); partitionMissingPrimary is pure and unit-tested.

import { listSheltersMissingPrimary, type ShelterRow } from "../data/shelters";
import { listShelterIdsWithActiveContacts } from "../data/userShelters";

export interface MissingPrimaryGroups<T> {
  hasContactsNoPrimary: T[];
  noContactsAtAll: T[];
}

// Split services that lack a primary into the two groups, keyed by which service
// ids have at least one active contact. Pure — no IO.
export function partitionMissingPrimary<T extends { id: string }>(
  missing: T[],
  shelterIdsWithContacts: Iterable<string>
): MissingPrimaryGroups<T> {
  const withContacts = new Set(shelterIdsWithContacts);
  const hasContactsNoPrimary: T[] = [];
  const noContactsAtAll: T[] = [];
  for (const s of missing) {
    if (withContacts.has(s.id)) hasContactsNoPrimary.push(s);
    else noContactsAtAll.push(s);
  }
  return { hasContactsNoPrimary, noContactsAtAll };
}

// Load and group the active services missing a primary contact. On a read error the
// underlying repos return null; we treat that as empty so the modal shows a clean,
// non-crashing "nothing to chase" rather than a spinner that never resolves.
export async function loadMissingPrimaryGroups(): Promise<MissingPrimaryGroups<ShelterRow>> {
  const missing = (await listSheltersMissingPrimary()) ?? [];
  const ids = missing.map((s) => s.id).filter((x): x is string => !!x);
  const withContacts = (await listShelterIdsWithActiveContacts(ids)) ?? [];
  return partitionMissingPrimary(missing, withContacts);
}
